import { Button, CircularProgress, IconButton } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import Dropzone from 'react-dropzone';
import CloseIcon from '@mui/icons-material/Close';
import { Show } from '~/app/components/core/show';
import Row from '~/ui/components/grid/row';
import { useHistory, useParams } from 'react-router-dom';
import cx from '~/data/utils/helpers/cx';
import { clientComparisonsPath, comparisonDetails } from '~/app/constants/url/auditor';
import { useQueryClient } from '@tanstack/react-query';
import { comparisonKeys, useCreateComparison, useUploadComparisonFile } from '~/data/comparisons/queries';
import { ComparisonFileRepresentation } from '~/data/openapi-client/index';
import ComparisonFileAutocomplete from './ComparisonFileAutocomplete';
import { formatAccept } from '~/app/utils/format-accept';

// Mirrors `ComparisonOptions.FileExtensions` on the backend (Dazzler.API/Contracts/ComparisonOptions.cs),
// which the server currently validates comparison uploads against. Add 'zip' here once xBRL-CSV
// comparison uploads are enabled server-side (that switch is flipped after the verification pass).
const COMPARISON_FILE_EXTENSIONS = ['xbrl', 'xml'];

const CompareFiles = () => {
  const { clientId } = useParams<{ clientId?: string }>();
  const history = useHistory();
  const [file1, setFile1] = useState<ComparisonFileRepresentation | null>(null);
  const [file2, setFile2] = useState<ComparisonFileRepresentation | null>(null);
  const [uploadedFile, setUploadedFile] = useState<{ name: string; error?: boolean } | undefined>(undefined);

  const queryClient = useQueryClient();

  const {
    mutate: uploadFile,
    reset: resetFileUpload,
    isLoading: isFileUploading,
  } = useUploadComparisonFile(clientId ?? '', {
    useErrorBoundary: false,
    onSuccess: (data) => {
      setFile2({ id: data.id, name: data.name, inserted: data.inserted });
      queryClient.invalidateQueries({
        queryKey: comparisonKeys.fileList(clientId ?? ''),
        exact: false,
        type: 'all',
      });
    },
    onError: () => {
      setUploadedFile((prev) => ({ ...prev, error: true }));
    },
  });
  const { mutate: createComparison } = useCreateComparison(clientId ?? '');

  return (
    <div>
      <div>
        <Row className="justify-between">
          <Typography fontSize={24} component="h1" className="text-dark">
            Compare files
          </Typography>
        </Row>
        <Typography fontSize={14} className="text-light">
          Comparison between 2 versions of the same type of report
        </Typography>
      </div>
      <div className="flex w-full mt-4 gap-4 items-center">
        <div className="basis-1/2">
          <Typography fontSize={14} className="text-light">
            File 1
          </Typography>
          <div className="p-5 bg-white rounded-md">
            <ComparisonFileAutocomplete
              clientId={clientId ?? ''}
              value={file1}
              onChange={setFile1}
              label="Choose file"
            />
          </div>
        </div>
        <div className="basis-1/2">
          <Typography fontSize={14} className="text-light">
            File 2
          </Typography>
          <div className="p-5 bg-white rounded-md flex items-center gap-2">
            <Show when={!uploadedFile}>
              <ComparisonFileAutocomplete
                clientId={clientId ?? ''}
                value={file2}
                onChange={setFile2}
                label="Choose file"
              />
            </Show>
            <Show when={!file2 && !uploadedFile}>
              <Typography>Or</Typography>
            </Show>
            <Dropzone
              accept={formatAccept(COMPARISON_FILE_EXTENSIONS)}
              onDrop={(acceptedFiles) => {
                const file = acceptedFiles[0];
                setUploadedFile({
                  name: file.name,
                });
                uploadFile({ file });
              }}
              multiple={false}
            >
              {({ getRootProps, getInputProps }) => (
                <Show
                  when={!uploadedFile}
                  fallback={
                    <div {...getRootProps()} className="grow">
                      <Show when={!uploadedFile}>
                        <input {...getInputProps()} />
                      </Show>
                      <ul className="flex flex-col gap-2 w-full">
                        <li
                          key={uploadedFile?.name}
                          className={cx(
                            'bg-secondary-main justify-between items-center px-4 py-2 rounded-md grid grid-cols-[1fr_auto] gap-2',
                            !!uploadedFile?.error && 'border-red border',
                            isFileUploading && 'grid-cols-[auto_1fr_auto]',
                          )}
                        >
                          <Show when={isFileUploading}>
                            <CircularProgress thickness={6} size={16} />
                          </Show>

                          <span className={cx('truncate', !!uploadedFile?.error ? 'text-red' : '')}>
                            {uploadedFile?.name}
                          </span>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isFileUploading) {
                                resetFileUpload();
                              }
                              setUploadedFile(undefined);
                              setFile2(null);
                            }}
                          >
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                        </li>
                      </ul>
                    </div>
                  }
                >
                  <div {...getRootProps()}>
                    <Show when={!uploadedFile}>
                      <input {...getInputProps()} />
                    </Show>
                    <Show when={!file2 && !uploadedFile}>
                      <Typography>
                        <span role="button" className="text-orange underline">
                          Upload
                        </span>
                      </Typography>
                    </Show>
                  </div>
                </Show>
              )}
            </Dropzone>
          </div>
        </div>
        <Button
          size="large"
          variant="contained"
          disabled={!file1 || !file2}
          className="mt-[21px]"
          onClick={() => {
            createComparison(
              {
                body: {
                  leftComparisonFileId: file1?.id,
                  rightComparisonFileId: file2?.id,
                },
              },
              {
                onSuccess: (data) => {
                  queryClient.invalidateQueries({
                    queryKey: comparisonKeys.clientComparisonList(clientId ?? ''),
                    exact: false,
                    type: 'all',
                  });
                  if (data.id) {
                    history.push(comparisonDetails(clientId, data.id));
                  } else {
                    history.push(clientComparisonsPath(clientId));
                  }
                },
              },
            );
          }}
        >
          Compare
        </Button>
      </div>
    </div>
  );
};

export default CompareFiles;
